import React from "react";
import { Col, FormControl, FormGroup, FormLabel, Row } from "react-bootstrap";
import { Navigate, useParams } from "react-router-dom";
import { ErrorMessages, InputBox, Loading, SavedMessage } from "../components";
import { ApiHelper, NavigationHelper, PendingCommand, TemplateInterface } from "../helpers";
import { Attachments, Questions } from "./components";

export const TemplatePage = () => {
  const { id } = useParams<{ id: string }>();
  const [template, setTemplate] = React.useState<TemplateInterface>(null);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [savedKey, setSavedKey] = React.useState("");
  const [questionsCommand, setQuestionsCommand] = React.useState<PendingCommand>(null);
  const [redirect, setRedirect] = React.useState("");

  const init = () => {
    if (id === "0") setTemplate({ isPublic: false, id: 0, seconds: 15, shapes: "16:9", templateType: "message", archived: false })
    else {
      ApiHelper.get("/templates/" + id).then((data: TemplateInterface) => {
        setTemplate(data)
      });
    }
    setIsSubmitting(false);
    setErrors(null);
  }

  React.useEffect(init, [id])


  const handleChange = (e: any) => {

    let t: TemplateInterface = { ...template };
    let val = e.currentTarget.value;

    switch (e.currentTarget.id) {
      case "seconds": t.seconds = parseInt(val, 0); break;
      case "isPublic": t.isPublic = val === "true"; break;
      case "shapes":
        const target: any = e.target;
        const shapeArray = [].slice.call(target.selectedOptions).map(item => item.value);
        t.shapes = shapeArray.join(",");
        break;
      case "name": t.name = val; break;
      case "html": t.html = val; break;
    }
    setTemplate(t);
  }

  const validate = () => {
    let result: string[] = [];
    if (template.name === "") result.push("Please enter a template name.");
    setErrors(result);
    return result.length === 0;
  }

  const handleSave = async () => {
    if (validate()) {
      setIsSubmitting(true);
      const data = await ApiHelper.post("/templates", [template]);
      setQuestionsCommand({ action: "save", parameter: { feedId: null, templateId: data[0].id } });
    }
  }

  const getDeleteFunction = () => (template?.id > 0) ? handleDelete : undefined

  const handleDelete = () => {
    if (window.confirm("Are you sure you wish to permanently delete this template?")) {
      ApiHelper.delete("/templates/" + template.id).then(() => {
        setRedirect("/settings/templates");
      });
    }
  }


  const questionsSaved = () => {
    setIsSubmitting(false);
    setSavedKey(NavigationHelper.getRefreshKey());
  }


  const getQuestions = () => {
    return <Questions templateId={template?.id} saveCallback={questionsSaved} pendingCommand={questionsCommand} />
  }

  const getAttachments = () => {
    return <Attachments contentType="templates" contentId={template?.id} />
  }

  if (redirect !== "") return <Navigate to={redirect} replace />
  else if (!template) return <Loading />
  else return (
    <>
      <h1><i className="fas fa-code"></i> Edit Template</h1>
      <p>Enter the Html for your template below. After saving the template you can upload images and other resources, and specify questions to be entered by the user. You can plug the answers to these questions values into the template by surrounding the key name in braces, such as {"{name}"}.</p>
      <Row>
        <Col md={6}>
          <InputBox headerIcon="fas fa-code" headerText="Edit Template" saveFunction={handleSave} isSubmitting={isSubmitting} deleteFunction={getDeleteFunction()}  >
            <SavedMessage refreshKey={savedKey} />
            <ErrorMessages errors={errors} />
            <Row>
              <Col>
                <FormGroup className="mb-3">
                  <FormLabel htmlFor="seconds">Display Seconds</FormLabel>
                  <FormControl id="seconds" value={template.seconds} onChange={handleChange} />
                </FormGroup>
              </Col>
              <Col>

                <FormLabel>Shapes</FormLabel>
                <FormControl id="shapes" size="sm" as="select" multiple value={template.shapes?.split(",")} onChange={handleChange} >
                  <option value="16:9">16:9</option>
                  <option value="9:16">9:16</option>
                  <option value="4:1">4:1</option>
                  <option value="1:4">1:4</option>
                </FormControl>

              </Col>
              <Col>
                <FormGroup className="mb-3">
                  <FormLabel>Is Public</FormLabel>
                  <FormControl as="select" id="isPublic" value={template.isPublic.toString()} onChange={handleChange}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </FormControl>
                </FormGroup>
              </Col>
            </Row>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormControl id="name" value={template.name} onChange={handleChange} />
            </FormGroup>
            <FormGroup className="mb-3">
              <FormLabel>HTML <span className="description">(Will be placed inside of &lt;body&gt;&lt;/body&gt; tag)</span></FormLabel>
              <FormControl as="textarea" id="html" value={template.html} onChange={handleChange} rows={8} />
            </FormGroup>
          </InputBox >
        </Col>
        <Col md={6}>
          {getQuestions()}
          {getAttachments()}
        </Col>
      </Row>
    </>
  );

};